import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { API_GET_WIDGET } from '../api/configurationScreenServices';
import { useLayout } from '../hooks/useLayout';
import { useBuilderDataContext } from '../hooks/BuilderDataContext';
import { addWidgetUtil, deleteWidgetUtil } from '../utils/previewPaneUtils';
import { formatWidgetConfig } from '../utils/globalUtils';

/**
 * Builder preview-pane state (replaces the Redux previewPane slice).
 *
 * Server layout data comes from the useLayout SWR cache (kept fresh by
 * useWidgetMutations / useSectionMutations via populateCache); this context
 * syncs it into local state and adds the ephemeral UI state on top
 * (selected section, dirty flag, draft/published toggle, demo widgets).
 *
 * Mounted in HomeScreen inside WidgetBuilderProvider/LabsPreviewProvider.
 */

const PreviewPaneContext = createContext(null);

const makeInitialSections = () => [{ sectionId: `section-${nanoid()}`, rows: [] }];

export function PreviewPaneProvider({ children }) {
  const builderData = useBuilderDataContext();
  const homepageId = builderData?.configId;
  const { layout, isLoading: isLayoutLoading } = useLayout(homepageId);

  const [sections, setSections] = useState(makeInitialSections);
  const [widgetConfiguration, setWidgetConfiguration] = useState({});
  const [selectedSection, setSelectedSection] = useState(0);
  const [isPreviewPaneDirty, setPreviewPaneDirty] = useState(false);
  const [showCurrentChanges, setShowCurrentChanges] = useState(true);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [smartViewCountRestrictionEnabled, setSmartViewCountRestrictionEnabled] = useState(false);

  const hasDraftChanges = layout?.hasDraftChanges || false;
  const draftLayout = layout?.draftLayout || null;
  const publishedLayout = layout?.mobileLayout || null;

  // Sync the SWR layout cache into local state (replaces the old
  // updateDraftAndPublishedLayout + setWidgetConfigurations bridge).
  useEffect(() => {
    if (!layout) return;
    const nextSections = layout.hasDraftChanges
      ? layout.draftLayout?.sections || layout.draftLayout || []
      : layout.mobileLayout?.sections || layout.mobileLayout || [];
    setSections(nextSections);
    setShowCurrentChanges(layout.hasDraftChanges ? true : false);
    if (layout.widgetConfiguration) {
      setWidgetConfiguration((prev) => ({
        ...prev,
        ...layout.widgetConfiguration
      }));
    }
    setSmartViewCountRestrictionEnabled(true);
  }, [layout]);

  // Ref of selectedSection for callbacks (avoids stale closures)
  const selectedSectionRef = React.useRef(selectedSection);
  useEffect(() => {
    selectedSectionRef.current = selectedSection;
  }, [selectedSection]);

  // ---- UI actions (former sync reducers) ----

  const updatePreviewPane = useCallback(
    ({ sections: newSections, selectedSection: newSelected }) => {
      if (newSections) setSections(newSections);
      if (newSelected !== undefined) setSelectedSection(newSelected);
    },
    []
  );

  const changeSelectedSection = useCallback((index) => {
    setSelectedSection(index);
  }, []);

  // Demo widget (client-only, e.g. banner/gallery/launcher placeholders)
  const addWidget = useCallback(({ widgetLayout, widgetConfig }) => {
    const { widgetId } = widgetLayout;
    setWidgetConfiguration((prev) => ({ ...prev, [widgetId]: widgetConfig }));
    setSections((prevSections) => {
      // Idempotent by widgetId: a placement id is unique, so re-adding the same
      // widgetId is always a duplicate. StrictMode double-invokes mount effects,
      // which made BannerSettings' CREATE-mode demo banner get placed twice (and
      // left a stray DEMO_BANNER after save → WIDGET_NOT_FOUND). Skip if present.
      const alreadyPlaced = prevSections.some((section) =>
        (section.rows || []).some((row) =>
          (row.widgets || []).some((widget) => widget.widgetId === widgetId)
        )
      );
      if (alreadyPlaced) return prevSections;
      const newPreviewPane = addWidgetUtil(
        { ...widgetLayout },
        { sections: [...prevSections] },
        selectedSectionRef.current
      );
      return newPreviewPane.sections;
    });
  }, []);

  const deleteWidget = useCallback(({ widgetId }) => {
    setWidgetConfiguration((prev) => {
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
    setSections((prevSections) => {
      const newPreviewPane = deleteWidgetUtil({ sections: [...prevSections] }, widgetId);
      return newPreviewPane.sections;
    });
  }, []);

  const updateWidgetConfiguration = useCallback(({ widget }) => {
    const { widgetId, config } = widget;
    setWidgetConfiguration((prev) => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], config }
    }));
  }, []);

  const addTopFixedSection = useCallback(() => {
    setSections((prevSections) => [
      { sectionId: `section-${nanoid()}`, rows: [], isFixed: true },
      ...prevSections
    ]);
    setSelectedSection(0);
    setPreviewPaneDirty(true);
  }, []);

  // ---- Draft/published toggle (former togglePreviewPaneView thunk) ----
  const togglePreviewPaneView = useCallback(
    async (nextShowCurrentChanges) => {
      setIsToggleLoading(true);
      try {
        const targetLayout =
          hasDraftChanges && nextShowCurrentChanges ? draftLayout : publishedLayout;
        const _sections = targetLayout?.sections || [];

        let _widgetConfig = { ...widgetConfiguration };
        const widgetIdList = [];

        _sections
          .flatMap((section) => section.rows || [])
          .flatMap((row) => row.widgets || [])
          .forEach((w) => {
            const widgetId = w?.widgetId;
            if (
              _widgetConfig[widgetId] === undefined ||
              (!nextShowCurrentChanges && !_widgetConfig[widgetId].publishedConfig)
            ) {
              widgetIdList.push(widgetId);
            } else {
              _widgetConfig[widgetId] = {
                ..._widgetConfig[widgetId],
                config: nextShowCurrentChanges
                  ? _widgetConfig[widgetId].draftConfig
                  : _widgetConfig[widgetId]?.publishedConfig
              };
            }
          });

        while (widgetIdList.length > 0) {
          const widgetBatch = widgetIdList.splice(0, 5);
          const widgetResponse = await Promise.allSettled(
            widgetBatch.map((widgetId) =>
              API_GET_WIDGET(widgetId).then((response) => response?.data)
            )
          );
          widgetResponse.forEach((w) => {
            if (w.value) {
              _widgetConfig[w.value.widgetId] = formatWidgetConfig(w.value, nextShowCurrentChanges);
            }
          });
        }

        setSections(_sections);
        setWidgetConfiguration(_widgetConfig);
        setShowCurrentChanges(nextShowCurrentChanges);
      } finally {
        setIsToggleLoading(false);
      }
    },
    [hasDraftChanges, draftLayout, publishedLayout, widgetConfiguration]
  );

  const loading = isToggleLoading || isLayoutLoading;

  // Shape mirrors the old Redux state so utils (previewPaneUtils) and the
  // mutation hooks keep receiving the object they expect.
  const previewPaneData = useMemo(
    () => ({
      sections,
      selectedSection,
      widgetConfiguration,
      isPreviewPaneDirty,
      hasDraftChanges,
      showCurrentChanges,
      draftLayout,
      publishedLayout,
      loading,
      isSaveLoading,
      smartViewCountRestrictionEnabled
    }),
    [
      sections,
      selectedSection,
      widgetConfiguration,
      isPreviewPaneDirty,
      hasDraftChanges,
      showCurrentChanges,
      draftLayout,
      publishedLayout,
      loading,
      isSaveLoading,
      smartViewCountRestrictionEnabled
    ]
  );

  const value = useMemo(
    () => ({
      ...previewPaneData,
      previewPaneData,
      updatePreviewPane,
      changeSelectedSection,
      setPreviewPaneDirty,
      addWidget,
      deleteWidget,
      updateWidgetConfiguration,
      addTopFixedSection,
      togglePreviewPaneView,
      setIsSaveLoading
    }),
    [
      previewPaneData,
      updatePreviewPane,
      changeSelectedSection,
      addWidget,
      deleteWidget,
      updateWidgetConfiguration,
      addTopFixedSection,
      togglePreviewPaneView
    ]
  );

  return <PreviewPaneContext.Provider value={value}>{children}</PreviewPaneContext.Provider>;
}

// Null-safe fallback (mirrors the old Redux initialState) so hooks like
// useWidgetEssentials keep working in builders that don't mount this
// provider (Object360, LeadControls) and in isolated tests.
const noop = () => {};
const fallbackData = {
  sections: [{ sectionId: 'section-fallback', rows: [] }],
  selectedSection: 0,
  widgetConfiguration: {},
  isPreviewPaneDirty: false,
  hasDraftChanges: false,
  showCurrentChanges: true,
  draftLayout: null,
  publishedLayout: null,
  loading: false,
  isSaveLoading: false,
  smartViewCountRestrictionEnabled: false
};
const fallback = {
  ...fallbackData,
  previewPaneData: fallbackData,
  updatePreviewPane: noop,
  changeSelectedSection: noop,
  setPreviewPaneDirty: noop,
  addWidget: noop,
  deleteWidget: noop,
  updateWidgetConfiguration: noop,
  addTopFixedSection: noop,
  togglePreviewPaneView: noop,
  setIsSaveLoading: noop
};

export function usePreviewPaneContext() {
  return useContext(PreviewPaneContext) ?? fallback;
}
